import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBrandInstagram,
  IconMail,
  IconPhone,
  IconSearch,
  IconUserSearch,
} from '@tabler/icons-react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

const STATUS_OPTIONS = [
  { value: 'new', label: 'Nueva' },
  { value: 'contacted', label: 'Contactado' },
  { value: 'interview', label: 'Entrevista' },
  { value: 'rejected', label: 'Descartado' },
  { value: 'hired', label: 'Contratado' },
];

const STATUS_META = {
  new: { label: 'Nueva', color: 'blue' },
  contacted: { label: 'Contactado', color: 'cyan' },
  interview: { label: 'Entrevista', color: 'violet' },
  rejected: { label: 'Descartado', color: 'gray' },
  hired: { label: 'Contratado', color: 'green' },
};

const formatDate = (value) => {
  if (!value) return 'Fecha pendiente';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Fecha pendiente';
  return date.toLocaleString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BarberApplications = () => {
  const { currentUser } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [draftStatus, setDraftStatus] = useState('new');
  const [draftNotes, setDraftNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const applicationsQuery = query(
      collection(db, 'barberApplications'),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(
      applicationsQuery,
      (snapshot) => {
        setApplications(snapshot.docs.map((applicationDoc) => ({
          id: applicationDoc.id,
          ...applicationDoc.data(),
        })));
        setLoading(false);
        setError('');
      },
      (snapshotError) => {
        console.error('Error al cargar postulaciones:', snapshotError);
        setError('No se pudieron cargar las postulaciones de barberos.');
        setLoading(false);
      }
    );
  }, []);

  const filteredApplications = useMemo(() => {
    const term = search.trim().toLowerCase();
    return applications.filter((application) => {
      const status = application.status || 'new';
      const searchable = [
        application.firstName,
        application.lastName,
        application.email,
        application.phone,
        application.instagram,
      ].join(' ').toLowerCase();
      return (!term || searchable.includes(term)) &&
        (statusFilter === 'all' || status === statusFilter);
    });
  }, [applications, search, statusFilter]);

  const stats = useMemo(() => ({
    total: applications.length,
    new: applications.filter((item) => (item.status || 'new') === 'new').length,
    interview: applications.filter((item) => item.status === 'interview').length,
    hired: applications.filter((item) => item.status === 'hired').length,
  }), [applications]);

  const openApplication = (application) => {
    setSelected(application);
    setDraftStatus(application.status || 'new');
    setDraftNotes(application.internalNotes || '');
  };

  const closeApplication = () => {
    if (saving) return;
    setSelected(null);
  };

  const saveApplication = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      await updateDoc(doc(db, 'barberApplications', selected.id), {
        status: draftStatus,
        internalNotes: draftNotes.trim().slice(0, 3000),
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || '',
        updatedByUid: currentUser?.uid || '',
      });
      notifications.show({
        title: 'Postulación actualizada',
        message: 'El estado y las notas quedaron guardados.',
        color: 'green',
      });
      setSelected(null);
    } catch (saveError) {
      console.error('Error al actualizar postulación:', saveError);
      notifications.show({
        title: 'No se pudo guardar',
        message: 'Revisa tu conexión e inténtalo nuevamente.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <div>
            <Text size="xs" fw={800} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.12em' }}>
              Reclutamiento
            </Text>
            <Title order={1} mt={4}>Postulaciones de barberos</Title>
            <Text c="dimmed" mt={6}>Perfiles recibidos desde la página web.</Text>
          </div>
          <Badge size="lg" variant="light" color="indigo">Solo administradores</Badge>
        </Group>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          {[
            ['Total', stats.total, 'dark'],
            ['Nuevas', stats.new, 'blue'],
            ['Entrevistas', stats.interview, 'violet'],
            ['Contratados', stats.hired, 'green'],
          ].map(([label, value, color]) => (
            <Card key={label} withBorder radius="lg" padding="md">
              <Text size="xs" c="dimmed" fw={700}>{label}</Text>
              <Text size="xl" fw={800} c={color} mt={4}>{value}</Text>
            </Card>
          ))}
        </SimpleGrid>

        <Card withBorder radius="xl" padding="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput
              label="Buscar"
              placeholder="Nombre, correo, teléfono o Instagram"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
            <Select
              label="Estado"
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || 'all')}
              data={[{ value: 'all', label: 'Todos los estados' }, ...STATUS_OPTIONS]}
            />
          </SimpleGrid>
        </Card>

        {error && <Alert icon={<IconAlertCircle size={18} />} color="red">{error}</Alert>}

        {loading ? (
          <Group justify="center" py="xl"><Loader /></Group>
        ) : filteredApplications.length === 0 ? (
          <Card withBorder radius="xl" padding="xl">
            <Stack align="center" gap="xs">
              <IconUserSearch size={36} color="gray" />
              <Text fw={700}>No hay postulaciones para mostrar</Text>
              <Text size="sm" c="dimmed">Prueba cambiando la búsqueda o el filtro.</Text>
            </Stack>
          </Card>
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="md">
            {filteredApplications.map((application) => {
              const status = STATUS_META[application.status || 'new'] || STATUS_META.new;
              const fullName = `${application.firstName || ''} ${application.lastName || ''}`.trim();
              return (
                <Card key={application.id} withBorder radius="xl" padding="lg">
                  <Stack gap="md" h="100%">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <div>
                        <Text fw={800} size="lg">{fullName || 'Sin nombre'}</Text>
                        <Text size="xs" c="dimmed" mt={3}>{formatDate(application.createdAt)}</Text>
                      </div>
                      <Badge color={status.color} variant="light">{status.label}</Badge>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {Number(application.yearsExperience || 0)} años de experiencia
                    </Text>
                    <Stack gap={7}>
                      <Text component="a" href={`mailto:${application.email}`} size="sm" c="blue" style={{ textDecoration: 'none' }}>
                        <IconMail size={15} style={{ verticalAlign: 'middle', marginRight: 7 }} />{application.email}
                      </Text>
                      <Text component="a" href={`tel:${application.phone}`} size="sm" c="blue" style={{ textDecoration: 'none' }}>
                        <IconPhone size={15} style={{ verticalAlign: 'middle', marginRight: 7 }} />{application.phone}
                      </Text>
                      <Text component="a" href={`https://instagram.com/${application.instagram}`} target="_blank" rel="noreferrer" size="sm" c="grape" style={{ textDecoration: 'none' }}>
                        <IconBrandInstagram size={15} style={{ verticalAlign: 'middle', marginRight: 7 }} />@{application.instagram}
                      </Text>
                    </Stack>
                    {application.internalNotes && (
                      <Text size="sm" lineClamp={2} c="dimmed">{application.internalNotes}</Text>
                    )}
                    <Button mt="auto" variant="light" onClick={() => openApplication(application)}>Ver y gestionar</Button>
                  </Stack>
                </Card>
              );
            })}
          </SimpleGrid>
        )}
      </Stack>

      <Modal opened={Boolean(selected)} onClose={closeApplication} title="Gestionar postulación" size="lg" centered>
        {selected && (
          <Stack>
            <div>
              <Title order={3}>{selected.firstName} {selected.lastName}</Title>
              <Text size="sm" c="dimmed" mt={4}>{formatDate(selected.createdAt)}</Text>
            </div>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Card withBorder radius="md" padding="sm"><Text size="xs" c="dimmed">Experiencia</Text><Text fw={700}>{selected.yearsExperience} años</Text></Card>
              <Card withBorder radius="md" padding="sm"><Text size="xs" c="dimmed">Origen</Text><Text fw={700}>{selected.source === 'website' ? 'Sitio web' : selected.source || 'Sin origen'}</Text></Card>
            </SimpleGrid>
            <Stack gap={8}>
              <Text component="a" href={`mailto:${selected.email}`} c="blue">{selected.email}</Text>
              <Text component="a" href={`tel:${selected.phone}`} c="blue">{selected.phone}</Text>
              <Text component="a" href={`https://instagram.com/${selected.instagram}`} target="_blank" rel="noreferrer" c="grape">@{selected.instagram}</Text>
            </Stack>
            <Select label="Estado" data={STATUS_OPTIONS} value={draftStatus} onChange={(value) => setDraftStatus(value || 'new')} />
            <Textarea
              label="Notas internas"
              description="Solo visibles para administradores. Máximo 3.000 caracteres."
              minRows={5}
              maxLength={3000}
              value={draftNotes}
              onChange={(event) => setDraftNotes(event.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={closeApplication} disabled={saving}>Cancelar</Button>
              <Button onClick={saveApplication} loading={saving}>Guardar cambios</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Container>
  );
};

export default BarberApplications;
